import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Filter, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

export default function SearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all'); // all, ra9184, ra12009, irr
  
  const filters = [
    { value: 'all', label: 'All References' },
    { value: 'ra9184', label: 'RA 9184' },
    { value: 'ra12009', label: 'RA 12009' },
    { value: 'irr', label: 'IRRs' }
  ];

  // Helper function to highlight search terms in text
  const highlightText = (text, searchQuery) => {
    if (!searchQuery || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchQuery.toLowerCase() ? 
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-600 font-semibold">{part}</mark> : 
        part
    );
  };

  // Helper function to get content snippet (150 characters around match)
  const getSnippet = (content, searchQuery) => {
    if (!content || !searchQuery) return content;
    
    const lowerContent = content.toLowerCase();
    const lowerQuery = searchQuery.toLowerCase();
    const index = lowerContent.indexOf(lowerQuery);
    
    if (index === -1) {
      // If no match, return first 150 chars
      return content.substring(0, 150) + (content.length > 150 ? '...' : '');
    }
    
    // Get 60 chars before and after the match
    const start = Math.max(0, index - 60);
    const end = Math.min(content.length, index + searchQuery.length + 90);
    
    let snippet = content.substring(start, end);
    if (start > 0) snippet = '...' + snippet;
    if (end < content.length) snippet = snippet + '...';
    
    return snippet;
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setQuery(value);

    if (value.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setSearching(true);
    try {
      const response = await api.get(`/search?q=${encodeURIComponent(value)}`);
      let filteredResults = response.data.results;
      
      // Apply reference filter
      if (selectedFilter !== 'all') {
        filteredResults = filteredResults.filter(result => {
          const refShortName = result.reference?.short_name?.toLowerCase() || '';
          if (selectedFilter === 'ra9184') {
            return refShortName.includes('ra 9184') || refShortName.includes('ra9184');
          } else if (selectedFilter === 'ra12009') {
            return refShortName.includes('ra 12009') || refShortName.includes('ra12009');
          } else if (selectedFilter === 'irr') {
            return refShortName.includes('irr');
          }
          return true;
        });
      }
      
      setResults(filteredResults);
      setShowResults(true);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setQuery('');

    if (result.type === 'reference') {
      navigate(`/references/${result.data.id}`);
    } else if (result.type === 'section') {
      navigate(`/sections/${result.data.id}`);
    } else if (result.type === 'provision') {
      navigate(`/provisions/${result.data.id}`);
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search provisions, sections, or laws..."
            value={query}
            onChange={handleSearch}
            onFocus={() => results.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            className="pl-10 pr-4 py-6 text-base dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder:text-slate-400"
            data-testid="universal-search-input"
          />
        </div>
        <Button
          variant={showFilters ? "default" : "outline"}
          onClick={() => setShowFilters(!showFilters)}
          className="px-4 py-6"
          title="Filter search results"
        >
          <Filter className="w-5 h-5" />
        </Button>
      </div>

      {/* Filter Options */}
      {showFilters && (
        <div className="absolute top-full mt-2 right-0 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-3 z-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Filter by Reference</span>
            <button onClick={() => setShowFilters(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {filters.map(filter => (
              <label
                key={filter.value}
                className="flex items-center gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded cursor-pointer"
              >
                <input
                  type="radio"
                  name="filter"
                  value={filter.value}
                  checked={selectedFilter === filter.value}
                  onChange={(e) => {
                    setSelectedFilter(e.target.value);
                    if (query.trim()) {
                      handleSearch({ target: { value: query } });
                    }
                  }}
                  className="text-emerald-600"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">{filter.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Search Results Dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 max-h-[500px] overflow-y-auto z-50">
          {/* Result count header */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              <span className="font-semibold text-emerald-600">{results.length}</span> result{results.length !== 1 && 's'} found
              {selectedFilter !== 'all' && (
                <span className="ml-2 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded text-xs">
                  {filters.find(f => f.value === selectedFilter)?.label}
                </span>
              )}
            </p>
          </div>

          {results.map((result, index) => (
            <div
              key={index}
              onClick={() => handleResultClick(result)}
              className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-100 dark:border-slate-700 last:border-b-0 transition-colors"
              data-testid={`search-result-${index}`}
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 capitalize shrink-0">
                  {result.type}
                </span>
                <div className="flex-1 min-w-0">
                  {result.type === 'reference' && (
                    <>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">
                        {highlightText(result.data.short_name, query)}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                        {highlightText(result.data.title, query)}
                      </p>
                    </>
                  )}
                  {result.type === 'section' && (
                    <>
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                        {result.reference?.short_name}
                      </p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">
                        {highlightText(result.data.number, query)}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                        {highlightText(result.data.title, query)}
                      </p>
                    </>
                  )}
                  {result.type === 'provision' && (
                    <>
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                        {result.reference?.short_name} → {result.section?.number}
                      </p>
                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                        {highlightText(getSnippet(result.data.content, query), query)}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && query.trim().length >= 2 && !searching && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 p-4 z-50">
          <p className="text-slate-600 dark:text-slate-400 text-center">No results found</p>
        </div>
      )}
    </div>
  );
}
